import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Flame, Mountain, Users, Server, Zap, InfinityIcon, Gamepad2, Shield, ChevronRight } from 'lucide-react';

const Home = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const features = [
    {
      icon: Users,
      title: 'Comunitate activă',
      description: 'Jucători dedicați, staff prezent și un mediu matur, orientat spre respect și colaborare.',
    },
    {
      icon: Server,
      title: 'Servere optimizate',
      description: 'Configurări gândite, lag minim, sisteme moderne și update-uri constante.',
    },
    {
      icon: Zap,
      title: 'Experiență modernă',
      description: 'Design curat, interfață modernă și o abordare tehnică actuală.',
    },
    {
      icon: InfinityIcon,
      title: 'Viziune pe termen lung',
      description: 'SimplyGods este un proiect în dezvoltare continuă, cu planuri solide pentru viitor.',
    },
  ];

  const servers = [
    {
      name: 'SimplyGods #Hades',
      game: 'FiveM',
      description: 'Serverul nostru de GTA 5: FiveM, construit pentru roleplay modern și stabilitate.',
      icon: Flame,
      path: '/hades',
      color: 'from-orange-500 to-red-600',
    },
    {
      name: 'SimplyGods #Ares',
      game: 'Hytale',
      description: 'Serverul nostru dedicat Hytale, gândit pentru explorare și creativitate.',
      icon: Mountain,
      path: '/ares',
      color: 'from-blue-500 to-purple-600',
    },
  ];

  return (
    <div ref={containerRef} className="relative min-h-screen">
      {/* Parallax Background */}
      <motion.div
        style={{ y }}
        className="absolute inset-0 pointer-events-none"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#fc6d00]/10 via-transparent to-transparent" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#fc6d00]/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-[#fc6d00]/10 rounded-full blur-[100px]" />
      </motion.div>

      {/* Centered Logo - Desktop only (smaller padding for Home) */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="hidden md:flex justify-center pt-20 pb-4"
      >
        <Link to="/" className="group">
          <motion.img
            whileHover={{ scale: 1.05 }}
            src="/logo.png"
            alt="SimplyGods"
            className="h-32 w-auto object-contain transition-all duration-300 group-hover:drop-shadow-[0_0_25px_rgba(252,109,0,0.6)]"
          />
        </Link>
      </motion.div>

      {/* Hero Section */}
      <motion.section
        style={{ opacity }}
        className="relative min-h-screen flex items-center justify-center pt-8 md:pt-4"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              {/* Badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10"
              >
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-sm text-[#b3b3c2]">Comunitate activă de gaming</span>
              </motion.div>

              {/* Title */}
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight"
              >
                Comunitatea{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#fc6d00] to-[#ff8c00]">
                  SimplyGods
                </span>
              </motion.h1>

              {/* Subtitle */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-lg md:text-xl text-[#b3b3c2] max-w-xl"
              >
                Servere moderne, configurate cu grijă, pentru jucători care vor stabilitate, 
                calitate și o comunitate serioasă.
              </motion.p>

              {/* Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="flex flex-wrap gap-4"
              >
                <Link to="/hades" className="btn-primary">
                  <Flame className="w-5 h-5 flex-shrink-0" />
                  <span>Intră pe SimplyGods #Hades</span>
                </Link>
                <Link to="/ares" className="btn-secondary">
                  <Mountain className="w-5 h-5 flex-shrink-0" />
                  <span>Descoperă SimplyGods #Ares</span>
                </Link>
              </motion.div>

              {/* Meta Info */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="flex flex-wrap gap-6 text-sm text-[#b3b3c2]"
              >
                <span className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#fc6d00]" />
                  Staff activ & comunitate implicată
                </span>
                <span className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-[#fc6d00]" />
                  Servere optimizate
                </span>
                <span className="flex items-center gap-2">
                  <InfinityIcon className="w-4 h-4 text-[#fc6d00]" />
                  Proiect pe termen lung
                </span>
              </motion.div>
            </motion.div>

            {/* Right Content - Server Cards */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="space-y-6"
            >
              <div className="glass rounded-2xl p-6 border border-white/10">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-[#fc6d00]/20 flex items-center justify-center">
                    <Gamepad2 className="w-6 h-6 text-[#fc6d00]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold">Serverele SimplyGods</h3>
                    <p className="text-sm text-[#b3b3c2]">Două experiențe diferite, aceeași calitate.</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {servers.map((server, index) => (
                    <motion.div
                      key={server.name}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 + index * 0.1 }}
                    >
                      <Link
                        to={server.path}
                        className="group block p-4 rounded-xl bg-white/5 border border-white/10 hover:border-[#fc6d00]/50 transition-all duration-300 hover:bg-white/10"
                      >
                        <div className="flex items-start gap-4">
                          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${server.color} flex items-center justify-center flex-shrink-0`}>
                            <server.icon className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h4 className="font-medium group-hover:text-[#fc6d00] transition-colors">
                                {server.name}
                              </h4>
                              <ChevronRight className="w-4 h-4 text-[#b3b3c2] group-hover:text-[#fc6d00] group-hover:translate-x-1 transition-all" />
                            </div>
                            <p className="text-sm text-[#b3b3c2] mt-1">{server.description}</p>
                            <span className="inline-block mt-2 text-xs px-2 py-1 rounded-full bg-white/10 text-[#b3b3c2]">
                              {server.game}
                            </span>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Features Section */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">De ce SimplyGods?</h2>
            <p className="text-[#b3b3c2] max-w-2xl mx-auto">
              O comunitate construită pe valori solide, cu focus pe calitate și experiența jucătorilor.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="group relative p-6 rounded-2xl bg-[#111119] border border-[#262636] hover:border-[#fc6d00]/50 transition-all duration-300"
              >
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#fc6d00]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                <div className="relative">
                  <div className="w-12 h-12 rounded-xl bg-[#fc6d00]/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <feature.icon className="w-6 h-6 text-[#fc6d00]" />
                  </div>
                  
                  <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-[#b3b3c2] leading-relaxed">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative p-8 md:p-12 rounded-3xl overflow-hidden"
          >
            {/* Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#fc6d00]/20 via-[#111119] to-[#111119]" />
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmYzZkMDAiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
            
            <div className="relative text-center space-y-6">
              <Shield className="w-16 h-16 text-[#fc6d00] mx-auto" />
              
              <h2 className="text-3xl md:text-4xl font-bold">
                Alătură-te comunității noastre
              </h2>
              
              <p className="text-[#b3b3c2] max-w-xl mx-auto">
                Fii parte din una dintre cele mai vechi și respectate comunități de gaming din România. 
                Te așteptăm cu brațele deschise!
              </p>
              
              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href="https://discord.gg/simplygods"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                >
                  Intră pe Discord
                </a>
                <Link to="/despre" className="btn-secondary">
                  Află mai multe
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
